/**
 * The file a workspace node or open artifact materialises as, so the tree and
 * the tab strip draw the same format marks as Case Files: folders are folders,
 * drafts (letters, briefs, the exhibit list, research memos) are Word
 * documents, and filled forms and the packet are PDFs. A saved agent plan,
 * case settings and key facts are not files.
 */
import type { Artifact, ArtifactKind } from "../types";
import type { WsNode } from "./types";

export type IconFile = {
  name?: string;
  mimeType?: string;
  isFolder: boolean;
};

export type FileIconKind = Extract<
  ArtifactKind | WsNode["kind"],
  "folder" | "draft" | "form" | "packet" | "plan"
>;

export const iconFileForKind = (kind: FileIconKind): IconFile | null => {
  switch (kind) {
    case "folder":
      return { isFolder: true };
    case "draft":
      return { name: "draft.docx", isFolder: false };
    case "form":
      return { name: "form.pdf", isFolder: false };
    case "packet":
      return { name: "packet.pdf", isFolder: false };
    case "plan":
    default:
      return null;
  }
};

/** The file a workspace row should draw. */
export const iconFileFor = (node: WsNode): IconFile | null =>
  iconFileForKind(node.kind);

/**
 * The file an open-artifact tab should draw. Case files use the filesystem
 * node when we have it (name + mime), otherwise the tab title. Settings and
 * plans have no file mark.
 */
export const iconFileForArtifact = (
  artifact: Pick<Artifact, "kind" | "title">,
  file?: {
    name: string;
    filename?: string;
    mime_type?: string | null;
    kind?: string;
  } | null
): IconFile | null => {
  if (artifact.kind === "file") {
    return {
      name: file?.filename ?? file?.name ?? artifact.title,
      mimeType: file?.mime_type ?? undefined,
      isFolder: file?.kind === "folder"
    };
  }
  if (
    artifact.kind === "settings" ||
    artifact.kind === "key_facts" ||
    artifact.kind === "time" ||
    artifact.kind === "company" ||
    artifact.kind === "workflows" ||
    artifact.kind === "card"
  )
    return null;
  return iconFileForKind(artifact.kind);
};
